import { useEffect, useState } from 'react';
import { Box, Tabs, Tab, Chip, Grid, Menu, MenuItem, Stack, Button } from '@mui/material';
import SearchToolbar from '../../components/SearchToolbar/SearchToolbar';
import SectionHeader from '../../components/SectionHeader';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../hooks/useIsMobile';
import NoTableDataImageSimp from '../../assets/image/empty-state-establecimientos-simp.png';
import NoTableDataImageBO from '../../assets/image/empty-state-establecimientos-backoffice.png';
import { useRoles } from '../../hooks/useRoles';
import { ConvocatedFilters } from './components/convocatedFilters';
import { useConvocados } from './hooks/useConvocados';
import ConvocatedGrid from './components/ConvocatedGrid';
import { PeriodicosFilters } from './components/PeriodicosFilters';
import PeriodicosDrawerManager, {
    PERIODICOS_DRAWER_ACTION_VALUES
} from './components/PeriodicosDrawerManager';
import { CircularProgress } from '@mui/material';
import { useConvocatorias } from './hooks/useConvocatorias';
import BackdropComponent from '../../components/Backdrop/BackdropComponent';

const ConvocadosIndex = ({ isHome }: { isHome?: boolean }) => {
    const { t } = useTranslation('periodicos');
    const { isAdminBO } = useRoles();
    const { isMobile, isTablet } = useIsMobile();
    const [openDrawer, setOpenDrawer] = useState('');
    const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const menuOpen = Boolean(anchorEl);

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, item: any) => {
        setAnchorEl(event.currentTarget);
        setSelectedEmployee(item);
    };

    const closeMenu = () => {
        setAnchorEl(null);
    };

    const {
        convocatedData,
        isFetching,
        filters,
        setFilters,
        extraFilters,
        handleChangeExtraFilters,
        handleSubmitSearch,
        clearFilters,
        idConvocatoria,
        handleExportarExcel,
        isExporting,
        descargarPdfConvocatoria,
        isDownloadingPdfConvocatoria,
        nombreOrganismoCentral,
        cuit
    } = useConvocados();

    const { handleGeneratePdfResultados, isGeneratingResultadosPdf } = useConvocatorias();

    const handleClickAction = (action: string, item: any) => {
        if (action === 'see-shift') {
            setSelectedEmployee(item);
            setOpenDrawer(PERIODICOS_DRAWER_ACTION_VALUES.ASSIGN_SHIFT);
        } else if (action === 'see-results') {
            setSelectedEmployee(item);
            setOpenDrawer(PERIODICOS_DRAWER_ACTION_VALUES.ASSIGN_RESULTS);
        }
    };

    const translate = (key: string): string => t(key) as string;
    const tabIndex = 3;
    return (
        <>
            <Grid container>
                <Grid item xs={12}>
                    <SectionHeader>
                        <SectionHeader.Title>
                            {cuit} {nombreOrganismoCentral}
                        </SectionHeader.Title>
                    </SectionHeader>
                </Grid>
                <Grid item xs={12}>
                    <SearchToolbar
                        inputSearchName=""
                        onChange={() => {}}
                        onClick={handleSubmitSearch}
                        showSearchInput={false}
                        clearFilters={clearFilters}
                    >
                        <PeriodicosFilters
                            extraFilters={extraFilters}
                            handleChangeExtraFilters={handleChangeExtraFilters}
                            tabIndex={tabIndex}
                        />
                    </SearchToolbar>
                </Grid>
            </Grid>
            <Grid container spacing={2} justifyContent="flex-end">
                <Grid item>
                    <Button
                        variant="contained"
                        color="primary"
                        sx={{ m: 1 }}
                        onClick={handleExportarExcel}
                        disabled={isExporting}
                        startIcon={
                            isExporting ? <CircularProgress size={20} color="inherit" /> : null
                        }
                    >
                        {t('actions.exportExcel')}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        sx={{ m: 1 }}
                        onClick={() => descargarPdfConvocatoria(idConvocatoria)}
                    >
                        {t('table.actions.generateCallPDF')}
                    </Button>

                    <Button
                        variant="contained"
                        color="primary"
                        sx={{ m: 1 }}
                        onClick={() => handleGeneratePdfResultados(idConvocatoria)}
                    >
                        {t('table.actions.generatePDFResults')}
                    </Button>
                </Grid>
            </Grid>
            <Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="flex-start"
                spacing={3}
            >
                {isMobile || isTablet ? (
                    ''
                ) : (
                    <Grid item xs={12}>
                        <Box sx={{ mb: 2 }}>
                            {(convocatedData?.data?.length === 0 ||
                                convocatedData?.data === undefined) &&
                            !isFetching ? (
                                <Box
                                    my={1}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    flexDirection="column"
                                >
                                    <Stack
                                        direction="column"
                                        justifyContent="center"
                                        alignItems="center"
                                        sx={{
                                            '& h4': { fontSize: '18px' },
                                            '& p': {
                                                maxWidth: '360px',
                                                textAlign: 'center',
                                                margin: 0,
                                                fontSize: '16px'
                                            }
                                        }}
                                    >
                                        <img
                                            src={
                                                isAdminBO
                                                    ? NoTableDataImageBO
                                                    : NoTableDataImageSimp
                                            }
                                            alt=""
                                        />
                                        <h4>{t('table.noData.title')}</h4>
                                        <p>{t('table.noData.description')}</p>
                                    </Stack>
                                </Box>
                            ) : (
                                <ConvocatedGrid
                                    items={convocatedData}
                                    filters={filters}
                                    setFilters={setFilters}
                                    isFetching={isFetching}
                                    translate={translate}
                                    handleClickAction={handleClickAction}
                                    handleOpenMenu={handleOpenMenu}
                                />
                            )}
                        </Box>
                    </Grid>
                )}
            </Grid>
            <Menu id="convocados-menu" anchorEl={anchorEl} open={menuOpen} onClose={closeMenu}>
                {[2, 3].includes(selectedEmployee?.examenEmpleado?.idEstadoEmpleadoExpuesto) && (
                    <MenuItem
                        onClick={() => {
                            closeMenu();
                            setOpenDrawer(PERIODICOS_DRAWER_ACTION_VALUES.ASSIGN_SHIFT);
                        }}
                    >
                        {t('table.actions.seeTurn')}
                    </MenuItem>
                )}

                {[4, 5].includes(selectedEmployee?.examenEmpleado?.idEstadoEmpleadoExpuesto) && (
                    <>
                        <MenuItem
                            onClick={() => {
                                closeMenu();
                                setOpenDrawer(PERIODICOS_DRAWER_ACTION_VALUES.ASSIGN_SHIFT);
                            }}
                        >
                            {t('table.actions.seeTurn')}
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                closeMenu();
                                setOpenDrawer(PERIODICOS_DRAWER_ACTION_VALUES.ASSIGN_RESULTS);
                            }}
                        >
                            {t('table.actions.seeResults')}
                        </MenuItem>
                    </>
                )}
            </Menu>

            <PeriodicosDrawerManager
                open={openDrawer}
                setOpen={setOpenDrawer}
                selectedIds={[selectedEmployee?.examenEmpleado?.id + '-convocado']}
                reloadGrid={handleSubmitSearch}
                selectedEmployee={selectedEmployee}
                reloadCurrentTab={handleSubmitSearch}
                tabIndex={tabIndex}
            />
            <BackdropComponent
                loading={isExporting || isDownloadingPdfConvocatoria || isGeneratingResultadosPdf}
            />
        </>
    );
};

export default ConvocadosIndex;
